/** @file Client setup files (spec §10.4): Claude Code .mcp.json, Codex config.toml, AGENTS snippet, README. */
import { PRODUCTION_URL } from './productionUrl';

/** One generated file: its name in the output directory and its text (LF line ends, final newline). */
export interface ClientSetupFile {
  path: string;
  text: string;
}

/** The server origin and the variables holding each client's token (defaults POSE2D_TOKEN_CLAUDE / _CODEX). */
export interface ClientSetupOptions {
  url?: string;
  claudeEnv?: string;
  codexEnv?: string;
}

const lines = (...rows: string[]): string => `${rows.join('\n')}\n`;

/** Claude Code: `${VAR}` in headers is expanded from the environment (verified, MCP clients spike). */
const claude = (url: string, env: string): string =>
  `${JSON.stringify({ mcpServers: { pose2d: { type: 'http', url: `${url}/mcp`, headers: { Authorization: `Bearer \${${env}}` }, timeout: 120000 } } }, null, 2)}\n`;

/** Codex: a hand-pasted snippet (`codex mcp add` rewrites the whole file and drops default-valued keys). */
const codex = (url: string, env: string): string =>
  lines('# Paste into ~/.codex/config.toml by hand: `codex mcp add` rewrites that file and drops default-valued keys.', '[mcp_servers.pose2d]', `url = "${url}/mcp"`, `bearer_token_env_var = "${env}"`, 'tool_timeout_sec = 120');

/** The section a game repo adds to its AGENTS.md. */
const agents = (url: string): string =>
  lines(
    '## Pose2D (2D skeletal animation)',
    '',
    `This project's characters live in Pose2D (${url}); the MCP server is \`pose2d\`.`,
    '',
    "- Call `get_guide('essentials')` first; topics: conventions, mixing, rig-from-psd, walk-cycle, directions, sockets, weights.",
    '- Loop: `get_summary` → `apply_commands` (send `ifRev`; one batch is one undo step) → `render_frame` / `render_animation` / `get_pose` → `revert` undoes.',
    "- Every commit records the name of the token that made it: use the token made for this agent, never another agent's.",
    '- `export` answers file URLs (a rig zip or a sprite sheet); heavy renders and exports can run on this machine with the `pose2d` CLI (`--local`).',
  );

/** How to install the files above and load the tokens. */
const readme = (url: string, claudeEnv: string, codexEnv: string): string =>
  lines(
    '# Pose2D client setup',
    '',
    'Generated by `pose2d setup` (tools/cli) from `@pose2d/api/clients/clientSetup`; do not edit by hand.',
    '',
    `Server: ${url} (MCP at ${url}/mcp, REST at ${url}/api/v1).`,
    '',
    '1. Ask TK for one token per agent (Settings, API tokens; scopes read, write, render, export). The token name is the actor of every commit it makes.',
    `2. Put it in the client's environment as ${claudeEnv} (Claude Code) or ${codexEnv} (Codex):`,
    `   - PowerShell: [Environment]::SetEnvironmentVariable('${claudeEnv}', '<token>', 'User'), then open a new terminal.`,
    `   - bash or zsh: export ${claudeEnv}=<token> in your shell profile.`,
    "   On TK's machine the bootstrap tokens are in tk.secret (POSE2D_TOKEN_CLAUDE, POSE2D_TOKEN_CODEX, POSE2D_TOKEN_CLI).",
    '3. Claude Code: copy claude-code.mcp.json to the game repo root as .mcp.json. Codex: paste codex.config.toml into ~/.codex/config.toml.',
    "4. Add AGENTS.snippet.md to the game repo's AGENTS.md.",
    '5. CLI (from the Pose2D repo): pnpm exec tsx tools/cli/src/main.ts login (reads POSE2D_TOKEN_CLI).',
    `6. Check: node tools/smoke/mcp-smoke.mjs --url ${url} --token-env ${claudeEnv} --project <name> lists the tools and renders a frame.`,
  );

/** The four files, for `pose2d setup` (docs/ai/clients) and the Settings "copy client configs" action (E7). */
export function clientSetup(opts: ClientSetupOptions = {}): ClientSetupFile[] {
  const url = (opts.url ?? PRODUCTION_URL).replace(/\/+$/, '');
  const claudeEnv = opts.claudeEnv ?? 'POSE2D_TOKEN_CLAUDE';
  const codexEnv = opts.codexEnv ?? 'POSE2D_TOKEN_CODEX';
  return [
    { path: 'claude-code.mcp.json', text: claude(url, claudeEnv) },
    { path: 'codex.config.toml', text: codex(url, codexEnv) },
    { path: 'AGENTS.snippet.md', text: agents(url) },
    { path: 'README.md', text: readme(url, claudeEnv, codexEnv) },
  ];
}
